import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Loader2 } from 'lucide-react';

/**
 * Composant pour la conversation avec l'assistant Aladinn
 * Affiche l'historique des messages et permet à l'utilisateur d'envoyer des messages
 */
const ObjectifConversation = ({
  messages = [],
  onSendMessage,
  isLoading = false,
  disabled = false
}) => {
  const [userInput, setUserInput] = useState('');
  const messagesEndRef = useRef(null);
  
  // Faire défiler vers le dernier message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Gérer l'envoi d'un message
  const handleSendMessage = () => {
    if (!userInput.trim() || isLoading || disabled) return;
    
    onSendMessage(userInput.trim());
    setUserInput('');
  };
  
  // Gérer la touche Entrée
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md flex-1 flex flex-col overflow-hidden">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-800">Conversation avec Aladinn</h2>
        </div>
      </div>
      
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {messages.map((message) => (
          <div 
            key={message.id}
            className={`flex ${message.type === 'bot' ? 'justify-start' : 'justify-end'}`}
          >
            <div 
              className={`max-w-[85%] rounded-2xl p-5 ${
                message.type === 'bot' 
                  ? 'bg-gradient-to-r from-blue-50 to-purple-50 text-gray-800' 
                  : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
              }`}
            >
              <p className="whitespace-pre-line text-base md:text-lg leading-relaxed">{message.text}</p>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-2xl p-5 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Zone de saisie du message */}
      <div className="border-t border-gray-100 p-5">
        <div className="flex items-center">
          <textarea
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Écrivez votre message..."
            className="flex-1 p-4 border border-gray-200 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-base md:text-lg"
            rows={2}
            disabled={isLoading || disabled}
          />
          <button
            onClick={handleSendMessage}
            disabled={!userInput.trim() || isLoading || disabled}
            className={`p-4 rounded-r-lg ${
              !userInput.trim() || isLoading || disabled
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
            }`}
            aria-label="Envoyer le message"
          >
            {isLoading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Send className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObjectifConversation;